import { useAuth } from 'contexts/auth'
import { Fragment, useEffect, useRef } from 'react'
import { useParams, useRouteMatch } from 'react-router-dom'
import api from 'services/api'
import useOrganizationStore, { OrganizationFromState } from 'store/header-organization'
import { formatCNPJ } from 'utils/is-valid-cnpj'
import { useStyles } from './styles'

interface RouteParams {
  organizationId?: string
  workspaceId?: string
}

const OrganizationInfoHeader = () => {
  const { organizationId, workspaceId } = useParams<RouteParams>()

  // O drill-down do contrato (/v2/workspaces/...) nao carrega organizationId na
  // rota, so o workspaceId. Sem esse ramo o header perde o cliente assim que o
  // usuario entra no contrato. O painel operacional tambem usa :workspaceId e
  // fica de fora de proposito — la o cabecalho e outro.
  const isWorkspaceRoute = Boolean(useRouteMatch('/v2/workspaces')) && Boolean(workspaceId)

  const classes = useStyles()

  const { user } = useAuth()

  const { organization, setOrganization, clearOrganization } = useOrganizationStore((state) => ({
    organization: state.organization,
    setOrganization: state.setOrganization,
    clearOrganization: state.clearOrganization,
  }))

  // Rota que originou a organizacao hoje exibida. Guardar isso (em vez de so
  // checar `!organization`) e o que faz o header trocar de cliente ao navegar
  // de uma organizacao/contrato para outra.
  const resolvedFor = useRef<string | null>(null)

  const sourceKey = organizationId ?? (isWorkspaceRoute ? workspaceId : undefined) ?? null

  useEffect(() => {
    if (user && !user.isSeller && user.organization) {
      return
    }

    if (!sourceKey) {
      resolvedFor.current = null
      clearOrganization()
      return
    }

    if (resolvedFor.current === sourceKey) {
      return
    }

    resolvedFor.current = sourceKey

    const request: Promise<OrganizationFromState | null> = organizationId
      ? api.get(`/organizations/${organizationId}`).then((response) => ({
          id: organizationId,
          name: response.data.data.corporateName,
          federalTaxId: response.data.data.federalTaxId,
        }))
      : api.get(`/v2/workspaces/${workspaceId}/get-details`).then((response) => {
          const buyer = response.data?.data?.buyerOrganization

          if (!buyer) {
            return null
          }

          return {
            id: buyer.id,
            name: buyer.corporateName,
            federalTaxId: buyer.federalTaxId,
          }
        })

    request
      .then((resolved) => {
        if (resolved) {
          setOrganization(resolved)
        }
      })
      .catch((error) => {
        // Libera a chave para uma nova tentativa na proxima navegacao.
        resolvedFor.current = null
        console.error(error)
      })
  }, [user, sourceKey, organizationId, workspaceId, setOrganization, clearOrganization])

  if (!user) {
    return null
  }

  if (organization === null) {
    return null
  }

  return (
    <Fragment>
      <hr className={classes.divisor} />
      <div className={classes.info}>
        <h1>{organization.name}</h1>
        {organization.federalTaxId && <span>{formatCNPJ(organization.federalTaxId)}</span>}
      </div>
    </Fragment>
  )
}

export default OrganizationInfoHeader
